import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Edit2, Trash2, Plus, Lock, Baby } from 'lucide-react';

const ProfileManager = ({ isOpen, onClose, profiles, onUpdateProfiles }) => {
  const [editingProfile, setEditingProfile] = useState(null);
  const [newProfile, setNewProfile] = useState({ name: '', type: 'adult' });

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setNewProfile({ name: profile.name, type: profile.type || 'adult' });
  };

  const handleSave = () => {
    if (editingProfile) {
      // Actualizar perfil existente
      const updated = profiles.map(p => 
        p.id === editingProfile.id 
          ? { ...p, name: newProfile.name, type: newProfile.type }
          : p
      );
      onUpdateProfiles(updated);
    } else {
      // Crear nuevo perfil
      const newProfileObj = {
        id: Date.now(),
        name: newProfile.name,
        type: newProfile.type,
        color: newProfile.type === 'kid' ? 'from-green-500 to-emerald-500' : 'from-blue-500 to-cyan-500'
      };
      onUpdateProfiles([...profiles, newProfileObj]);
    }
    setEditingProfile(null);
    setNewProfile({ name: '', type: 'adult' });
  };

  const handleDelete = (profileId) => {
    if (profiles.length > 1) {
      onUpdateProfiles(profiles.filter(p => p.id !== profileId));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="relative w-full max-w-2xl bg-gray-900 rounded-2xl border border-gray-700/50 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-700/50">
              <h2 className="text-2xl font-bold text-white">Administrar Perfiles</h2>
              <p className="text-gray-400 mt-1">Gestiona los perfiles de tu cuenta</p>
            </div>

            {/* Perfiles Existentes */}
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {profiles.map(profile => (
                <div key={profile.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${profile.color} rounded-full flex items-center justify-center`}>
                      {profile.type === 'kid' ? 
                        <Baby className="w-6 h-6 text-white" /> : 
                        <User className="w-6 h-6 text-white" />
                      }
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{profile.name}</h3>
                      <p className="text-gray-400 text-sm capitalize">{profile.type}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <motion.button
                      onClick={() => handleEdit(profile)}
                      className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </motion.button>
                    {profiles.length > 1 && (
                      <motion.button
                        onClick={() => handleDelete(profile.id)}
                        className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Formulario de Edición/Creación */}
            <div className="p-6 border-t border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">
                {editingProfile ? 'Editar Perfil' : 'Crear Nuevo Perfil'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-gray-300 text-sm font-medium">Nombre del perfil</label>
                  <input
                    type="text"
                    value={newProfile.name}
                    onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-red-500"
                    placeholder="Ingresa el nombre"
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm font-medium">Tipo de perfil</label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="adult"
                        checked={newProfile.type === 'adult'}
                        onChange={(e) => setNewProfile({...newProfile, type: e.target.value})}
                        className="text-red-500 focus:ring-red-500"
                      />
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-300">Adulto</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="kid"
                        checked={newProfile.type === 'kid'}
                        onChange={(e) => setNewProfile({...newProfile, type: e.target.value})}
                        className="text-red-500 focus:ring-red-500"
                      />
                      <Baby className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-300">Infantil</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-3">
                  <motion.button
                    onClick={handleSave}
                    disabled={!newProfile.name.trim()}
                    className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 rounded-lg font-semibold transition-colors"
                    whileHover={{ scale: !newProfile.name.trim() ? 1 : 1.02 }}
                  >
                    {editingProfile ? 'Actualizar' : 'Crear'} Perfil
                  </motion.button>
                  {editingProfile && (
                    <motion.button
                      onClick={() => {
                        setEditingProfile(null);
                        setNewProfile({ name: '', type: 'adult' });
                      }}
                      className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                      whileHover={{ scale: 1.02 }}
                    >
                      Cancelar
                    </motion.button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProfileManager;